import { createApi } from "@reduxjs/toolkit/query/react";

import { getNameFromPath } from "../utils";
import {
  Check2FAStatusResponse,
  ContentPerformanceAuthorRow,
  ContentPerformanceBookRow,
  ContentPerformanceCultureCategoryRow,
  ContentPerformanceCultureRow,
  ContentPerformanceListParams,
  ContentPerformanceListResponse,
  ContentPerformanceQuoteRow,
  ContentPerformanceSummaryResponse,
  ContentPerformanceTagRow,
  CreateAuthorParams,
  CreateCultureCategoryParams,
  CreateCultureParams,
  CreateLanguageParams,
  CreateQuoteParams,
  CreateQuoteTranslationParams,
  CreateReferenceParams,
  CreateSourceParams,
  CreateTagParams,
  DashboardResponse,
  DeleteQuoteTranslationParams,
  DeleteTagParams,
  Disable2FAParams,
  EditAuthorParams,
  EditCultureCategoryParams,
  EditCultureParams,
  EditLanguageParams,
  EditQuoteParams,
  EditQuoteTranslationParams,
  EditReferenceParams,
  EditSourceParams,
  EditTagParams,
  Enable2FAParams,
  Enable2FAResponse,
  GetAllSettingsResponse,
  GetAuthorsParams,
  GetCultureCategoriesParams,
  GetCultureQuotesParams,
  GetCulturesParams,
  GetDashboardParams,
  GetEventsParams,
  GetEventsResponse,
  GetEventStatsParams,
  GetEventStatsResponse,
  GetLanguagesParams,
  GetSystemHealthParams,
  GetQuotesParams,
  GetQuoteTranslationsParams,
  GetReferencesParams,
  GetSourcesParams,
  GetTagQuotesParams,
  GetTagsParams,
  GetUserLoginHistoryParams,
  GetUserLoginHistoryResponse,
  LoginParams,
  LoginResponse,
  RegenerateBackupCodesParams,
  RegenerateBackupCodesResponse,
  RegisterUserParams,
  Setup2FAResponse,
  SystemHealthResponse,
  UpdateProfileParams,
  UpdateSettingParams,
  UpdateSettingResponse,
  UserParams,
  UserUpdateParams,
  Verify2FAParams,
} from "./api.types";
import { axiosBaseQuery } from "./axiosBaseQuery";

const baseUrl = "/api";

const providerTags = [
  "Users",
  "User",
  "Authors",
  "Tags",
  "References",
  "Cultures",
  "CultureCategories",
  "Sources",
  "Languages",
  "QueryTranslations",
  "Quotes",
  "Dashboard",
  "ContentPerformance",
  "Settings",
];

const defaultSortParams = {
  sortBy: "updatedAt",
  sortOrder: "DESC",
};

// Convert an array of roles into a query parameter string format.
// This function takes an array of roles and transforms it into a format
// suitable for inclusion in a URL query string.
// Example: roles: ["Admin", "Creator"] => "roles[]=Admin&roles[]=Creator"
const rolesToQueryParam = (roles: string[]) => {
  return roles.map(role => `roles[]=${role}`).join("&");
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: axiosBaseQuery({ baseUrl }),
  tagTypes: providerTags,
  endpoints: builder => ({
    // Auth
    login: builder.mutation<LoginResponse, LoginParams>({
      query: ({ email, password, ipData }) => {
        // Set default values to "Unknown" for IP-related headers in case ipData is undefined.
        // Using "Unknown" helps to clearly indicate that the IP information could not be retrieved,
        // which can be useful for debugging and logging purposes.
        const unKnownText = "Unknown";
        const headers: Record<string, string> = {
          "x-user-ip": ipData?.ip || unKnownText,
          "x-user-city": ipData?.city || unKnownText,
          "x-user-country": ipData?.country_name || unKnownText,
        };

        return {
          url: "/auth/login",
          method: "post",
          data: { email, password },
          headers,
        };
      },
      invalidatesTags: providerTags,
    }),

    logout: builder.mutation<any, void>({
      query: () => ({
        url: "/auth/logout",
        method: "post",
      }),
      invalidatesTags: providerTags,
    }),

    refresh: builder.mutation<any, void>({
      query: () => ({
        url: "/auth/refresh",
        method: "post",
      }),
    }),

    getUser: builder.query<any, any>({
      query: params => ({
        url: "/auth/user",
        method: "get",
        params,
      }),
      providesTags: ["User"],
    }),

    registerUser: builder.mutation<any, RegisterUserParams>({
      query: body => ({
        url: "/auth/register",
        method: "post",
        data: body,
      }),
      // We do not invalidate any tags during user registration because an immediate
      // follow-up request is made to add roles using the updateUser mutation.
      // The user creation API does not accept roles as a parameter, so roles are added
      // in a separate request. Tag invalidation is handled in the updateUser mutation
      // to ensure that the cache is updated correctly after user is created and role(s) are assigned.
      invalidatesTags: [],
    }),

    // Users
    getUsers: builder.query<any, UserParams>({
      query: ({ roles, ...otherParams }) => {
        return {
          url: roles ? `/users?${rolesToQueryParam(roles)}` : "/users",
          method: "get",
          params: otherParams,
        };
      },
      providesTags: ["Users"],
    }),

    updateUser: builder.mutation<any, UserUpdateParams>({
      query: ({ id, ...otherParams }) => ({
        url: `/users/${id}`,
        method: "put",
        // Remove undefined or empty values from the object
        data: Object.fromEntries(Object.entries(otherParams).filter(([_, v]) => v != null && v !== "")),
      }),
      invalidatesTags: ["Users", "User"],
    }),

    getUserLoginHistory: builder.query<GetUserLoginHistoryResponse, GetUserLoginHistoryParams>({
      query: ({ id, ...params }) => ({
        url: `/users/login-history?userId=${id}`,
        method: "get",
        params,
      }),
    }),

    // Profile
    updateProfile: builder.mutation<any, UpdateProfileParams>({
      query: ({ id, ...data }) => ({
        url: `/users/${id}/profile`,
        method: "put",
        data,
      }),
    }),

    // Quotes
    getQuotes: builder.query<any, GetQuotesParams>({
      query: params => ({
        url: "/quotes",
        method: "get",
        params: { ...defaultSortParams, ...params },
      }),
      providesTags: ["Quotes"],
    }),

    editQuote: builder.mutation<any, EditQuoteParams>({
      query: ({ id, ...data }) => ({
        url: `/quotes/${id}`,
        method: "put",
        data,
      }),
      invalidatesTags: ["Quotes"],
    }),

    createQuote: builder.mutation<any, CreateQuoteParams>({
      query: data => ({
        url: "/quotes",
        method: "post",
        data,
      }),
      invalidatesTags: ["Quotes"],
    }),

    deleteQuote: builder.mutation<any, { id: number }>({
      query: ({ id }) => ({
        url: `/quotes/${id}`,
        method: "delete",
      }),
      invalidatesTags: ["Quotes"],
    }),

    // tags
    getTags: builder.query<any, GetTagsParams>({
      query: params => ({
        url: "/tags",
        method: "get",
        params,
      }),
      providesTags: ["Tags"],
    }),

    createTag: builder.mutation<any, CreateTagParams>({
      query: data => ({
        url: "/tags",
        method: "post",
        data,
      }),
      invalidatesTags: ["Tags"],
    }),

    editTag: builder.mutation<any, EditTagParams>({
      query: ({ id, ...otherData }) => ({
        url: `/tags/${id}`,
        method: "put",
        data: otherData,
      }),
      invalidatesTags: ["Tags"],
    }),

    deleteTag: builder.mutation<any, DeleteTagParams>({
      query: ({ id }) => ({
        url: `/tags/${id}`,
        method: "delete",
      }),
      invalidatesTags: ["Tags"],
    }),

    getTagQuotes: builder.query<any, GetTagQuotesParams>({
      query: ({ id, ...params }) => ({
        url: `/tags/${id}/quotes`,
        method: "get",
        params,
      }),
    }),

    // authors
    getAuthors: builder.query<any, GetAuthorsParams>({
      query: params => ({
        url: "/authors",
        method: "get",
        params,
      }),
      providesTags: ["Authors"],
    }),

    createAuthor: builder.mutation<any, CreateAuthorParams>({
      query: ({ name, published }) => ({
        url: "/authors",
        method: "post",
        data: { name, published },
      }),
      invalidatesTags: ["Authors"],
    }),

    editAuthor: builder.mutation<any, EditAuthorParams>({
      query: ({ name, id, published }) => ({
        url: `/authors/${id}`,
        method: "put",
        data: { name, published },
      }),
      invalidatesTags: ["Authors", "User"],
    }),

    deleteAuthor: builder.mutation<any, { id: number | string }>({
      query: ({ id }) => ({
        url: `/authors/${id}`,
        method: "delete",
      }),
      invalidatesTags: ["Authors"],
    }),

    // References
    getReferences: builder.query<any, GetReferencesParams>({
      query: params => ({
        url: "/refs",
        method: "get",
        params,
      }),
      providesTags: ["References"],
    }),

    createReference: builder.mutation<any, CreateReferenceParams>({
      query: ({ text, published }) => ({
        url: "/refs",
        method: "post",
        data: { text, published },
      }),
      invalidatesTags: ["References"],
    }),

    editReference: builder.mutation<any, EditReferenceParams>({
      query: ({ text, id, published }) => ({
        url: `/refs/${id}`,
        method: "put",
        data: { text, published },
      }),
      invalidatesTags: ["References"],
    }),

    deleteReference: builder.mutation<any, { id: string | number }>({
      query: ({ id }) => ({
        url: `/refs/${id}`,
        method: "delete",
      }),
      invalidatesTags: ["References"],
    }),

    // Cultures
    getCultures: builder.query<any, GetCulturesParams>({
      query: params => ({
        url: "/cultures",
        method: "get",
        params,
      }),
      providesTags: ["Cultures"],
    }),

    createCulture: builder.mutation<any, CreateCultureParams>({
      query: ({ cultureCategoryIds, ...body }) => ({
        url: "/cultures",
        method: "post",
        data: { ...body, cultureCategoryIds },
      }),
      invalidatesTags: ["Cultures"],
    }),

    // When updating a culture, the service expects only the file name of the icon, not the full path.
    // The fetch cultures data from the service returns the full path of the icon.
    // To ensure we send back only the file name when updating the culture, we extract the file name from the path.
    // This is handled globally here to maintain consistency across the application.
    editCulture: builder.mutation<any, EditCultureParams>({
      query: ({ id, icon, culture, published, cultureCategoryIds }) => ({
        url: `/cultures/${id}`,
        method: "put",
        data: { icon: getNameFromPath(icon), culture, published, cultureCategoryIds },
      }),
      invalidatesTags: ["Cultures"],
    }),

    deleteCulture: builder.mutation<any, { id: string | number }>({
      query: ({ id }) => ({
        url: `/cultures/${id}`,
        method: "delete",
      }),
      invalidatesTags: ["Cultures"],
    }),

    getCultureQuotes: builder.query<any, GetCultureQuotesParams>({
      query: ({ id, ...params }) => ({
        url: `/cultures/${id}/quotes`,
        method: "get",
        params,
      }),
    }),

    // Culture Categories
    getCultureCategories: builder.query<any, GetCultureCategoriesParams>({
      query: params => ({
        url: "/culture-categories",
        method: "get",
        params,
      }),
      providesTags: ["CultureCategories"],
    }),

    createCultureCategory: builder.mutation<any, CreateCultureCategoryParams>({
      query: body => ({
        url: "/culture-categories",
        method: "post",
        data: body,
      }),
      invalidatesTags: ["CultureCategories"],
    }),

    editCultureCategory: builder.mutation<any, EditCultureCategoryParams>({
      query: ({ id, ...body }) => ({
        url: `/culture-categories/${id}`,
        method: "put",
        data: body,
      }),
      invalidatesTags: ["CultureCategories"],
    }),

    deleteCultureCategory: builder.mutation<any, { id: string | number }>({
      query: ({ id }) => ({
        url: `/culture-categories/${id}`,
        method: "delete",
      }),
      invalidatesTags: ["CultureCategories"],
    }),

    // Sources
    getSources: builder.query<any, GetSourcesParams>({
      query: params => ({
        url: "/books",
        method: "get",
        params,
      }),
      providesTags: ["Sources"],
    }),

    createSource: builder.mutation<any, CreateSourceParams>({
      query: body => ({
        url: "/books",
        method: "post",
        data: body,
      }),
      invalidatesTags: ["Sources"],
    }),

    // When updating, the service expects only the file name of the icon, not the full path.
    // The fetch source data from the service returns the full path of the icon.
    // To ensure we send back only the file name when updating the source, we extract the file name from the path.
    // This is handled globally here to maintain consistency across the application.
    editSource: builder.mutation<any, EditSourceParams>({
      query: ({ id, name, isbn, authors, published, cover }) => ({
        url: `/books/${id}`,
        method: "put",
        data: { name, isbn, authors, published, cover: getNameFromPath(cover ?? "") },
      }),
      invalidatesTags: ["Sources"],
    }),

    deleteSource: builder.mutation<any, { id: string | number }>({
      query: ({ id }) => ({
        url: `/books/${id}`,
        method: "delete",
      }),
      invalidatesTags: ["Sources"],
    }),

    // Languages
    getLanguages: builder.query<any, GetLanguagesParams>({
      query: params => ({
        url: "/langs",
        method: "get",
        params,
      }),
      providesTags: ["Languages"],
    }),

    createLanguage: builder.mutation<any, CreateLanguageParams>({
      query: ({ lang, published }) => ({
        url: "/langs",
        method: "post",
        data: { published, lang: lang.trim() },
      }),
      invalidatesTags: ["Languages"],
    }),

    editLanguage: builder.mutation<any, EditLanguageParams>({
      query: ({ id, lang, published }) => ({
        url: `/langs/${id}`,
        method: "put",
        data: { lang: lang.trim(), published },
      }),
      invalidatesTags: ["Languages"],
    }),

    deleteLanguage: builder.mutation<any, { id: string | number }>({
      query: ({ id }) => ({
        url: `/langs/${id}`,
        method: "delete",
      }),
      invalidatesTags: ["Languages"],
    }),

    // Query Translations
    getQuoteTranslations: builder.query<any, GetQuoteTranslationsParams>({
      query: ({ quoteId }) => ({
        url: `/quotes/${quoteId}/trans`,
        method: "get",
      }),
      providesTags: ["QueryTranslations"],
    }),

    createQuoteTranslation: builder.mutation<any, CreateQuoteTranslationParams>({
      query: ({ quoteId, lang, text, refs }) => ({
        url: `/quotes/${quoteId}/trans`,
        method: "post",
        data: { lang, text, refs },
      }),
      invalidatesTags: ["QueryTranslations", "Quotes"],
    }),

    editQuoteTranslation: builder.mutation<any, EditQuoteTranslationParams>({
      query: ({ quoteId, id, ...otherParams }) => ({
        url: `/quotes/${quoteId}/trans/${id}`,
        method: "put",
        data: otherParams,
      }),
      invalidatesTags: ["QueryTranslations", "Quotes"],
    }),

    deleteQuoteTranslation: builder.mutation<any, DeleteQuoteTranslationParams>({
      query: ({ quoteId, id }) => ({
        url: `/quotes/${quoteId}/trans/${id}`,
        method: "delete",
      }),
      invalidatesTags: ["QueryTranslations", "Quotes"],
    }),

    // 2FA endpoints
    verify2FA: builder.mutation<LoginResponse, Verify2FAParams>({
      query: ({ tempToken, code }) => ({
        url: "/auth/2fa/verify",
        method: "post",
        data: { tempToken, code },
      }),
      invalidatesTags: providerTags,
    }),

    check2FAStatus: builder.query<Check2FAStatusResponse, void>({
      query: () => ({
        url: "/auth/2fa/status",
        method: "get",
      }),
    }),

    setup2FA: builder.mutation<Setup2FAResponse, void>({
      query: () => ({
        url: "/auth/2fa/setup",
        method: "post",
      }),
    }),

    enable2FA: builder.mutation<Enable2FAResponse, Enable2FAParams>({
      query: ({ token, setupToken }) => ({
        url: "/auth/2fa/enable",
        method: "post",
        data: { token, setupToken },
      }),
      invalidatesTags: ["Users"],
    }),

    disable2FA: builder.mutation<any, Disable2FAParams>({
      query: ({ token }) => ({
        url: "/auth/2fa/disable",
        method: "post",
        data: { token },
      }),
      invalidatesTags: ["Users"],
    }),

    regenerateBackupCodes: builder.mutation<RegenerateBackupCodesResponse, RegenerateBackupCodesParams>({
      query: ({ token }) => ({
        url: "/auth/2fa/backup-codes",
        method: "post",
        data: { token },
      }),
    }),

    // Dashboard
    getDashboard: builder.query<DashboardResponse, GetDashboardParams>({
      query: params => ({
        url: "/dashboard",
        method: "get",
        params,
      }),
      providesTags: ["Dashboard"],
    }),

    // Content performance (v2)
    getContentPerformanceSummary: builder.query<ContentPerformanceSummaryResponse, ContentPerformanceListParams>({
      query: params => ({
        url: "/v2/content-performance/summary",
        method: "get",
        params,
      }),
      providesTags: ["ContentPerformance"],
    }),

    getContentPerformanceQuotes: builder.query<ContentPerformanceListResponse<ContentPerformanceQuoteRow>, ContentPerformanceListParams>({
      query: params => ({
        url: "/v2/content-performance/quotes",
        method: "get",
        params,
      }),
      providesTags: ["ContentPerformance"],
    }),

    getContentPerformanceAuthors: builder.query<ContentPerformanceListResponse<ContentPerformanceAuthorRow>, ContentPerformanceListParams>({
      query: params => ({
        url: "/v2/content-performance/authors",
        method: "get",
        params,
      }),
      providesTags: ["ContentPerformance"],
    }),

    getContentPerformanceBooks: builder.query<ContentPerformanceListResponse<ContentPerformanceBookRow>, ContentPerformanceListParams>({
      query: params => ({
        url: "/v2/content-performance/books",
        method: "get",
        params,
      }),
      providesTags: ["ContentPerformance"],
    }),

    getContentPerformanceCultureCategories: builder.query<
      ContentPerformanceListResponse<ContentPerformanceCultureCategoryRow>,
      ContentPerformanceListParams
    >({
      query: params => ({
        url: "/v2/content-performance/culture-categories",
        method: "get",
        params,
      }),
      providesTags: ["ContentPerformance"],
    }),

    getContentPerformanceCultures: builder.query<ContentPerformanceListResponse<ContentPerformanceCultureRow>, ContentPerformanceListParams>({
      query: params => ({
        url: "/v2/content-performance/cultures",
        method: "get",
        params,
      }),
      providesTags: ["ContentPerformance"],
    }),

    getContentPerformanceTags: builder.query<ContentPerformanceListResponse<ContentPerformanceTagRow>, ContentPerformanceListParams>({
      query: params => ({
        url: "/v2/content-performance/tags",
        method: "get",
        params,
      }),
      providesTags: ["ContentPerformance"],
    }),

    // Events (for Dashboard V2)
    getEvents: builder.query<GetEventsResponse, GetEventsParams>({
      query: params => ({
        url: "/events",
        method: "get",
        params,
      }),
    }),
    getEventStats: builder.query<GetEventStatsResponse, GetEventStatsParams>({
      query: params => ({
        url: "/events/stats",
        method: "get",
        params,
      }),
    }),

    // Admin settings
    getAllSettings: builder.query<GetAllSettingsResponse, void>({
      query: () => ({
        url: "/admin/settings",
        method: "get",
      }),
      providesTags: ["Settings"],
    }),
    updateSetting: builder.mutation<UpdateSettingResponse, UpdateSettingParams>({
      query: ({ key, value }) => ({
        url: `/admin/settings/${encodeURIComponent(key)}`,
        method: "put",
        data: { value },
      }),
      invalidatesTags: ["Settings"],
    }),
    resetTrial: builder.mutation<{ status: string; userId: string }, string>({
      query: (userId) => ({
        url: `/admin/trial/${userId}`,
        method: "delete",
      }),
      invalidatesTags: ["Users"],
    }),

    // System Health
    getSystemHealth: builder.query<SystemHealthResponse, GetSystemHealthParams>({
      query: params => ({
        url: "/system-health",
        method: "get",
        params,
      }),
    }),
  }),
});

export const {
  // Auth
  useLoginMutation,
  useLogoutMutation,
  useRefreshMutation,
  useGetUserQuery,
  useRegisterUserMutation,

  // User
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useUpdateUserMutation,
  useUpdateProfileMutation,
  useGetUserLoginHistoryQuery,

  // Tags
  useGetTagsQuery,
  useCreateTagMutation,
  useEditTagMutation,
  useDeleteTagMutation,
  useGetTagQuotesQuery,

  // Authors
  useGetAuthorsQuery,
  useCreateAuthorMutation,
  useEditAuthorMutation,
  useDeleteAuthorMutation,

  // References
  useGetReferencesQuery,
  useCreateReferenceMutation,
  useEditReferenceMutation,
  useDeleteReferenceMutation,

  // Cultures
  useGetCulturesQuery,
  useCreateCultureMutation,
  useEditCultureMutation,
  useDeleteCultureMutation,
  useGetCultureQuotesQuery,

  // Culture Categories
  useGetCultureCategoriesQuery,
  useCreateCultureCategoryMutation,
  useEditCultureCategoryMutation,
  useDeleteCultureCategoryMutation,

  // Sources
  useGetSourcesQuery,
  useCreateSourceMutation,
  useEditSourceMutation,
  useDeleteSourceMutation,

  // Languages
  useGetLanguagesQuery,
  useCreateLanguageMutation,
  useEditLanguageMutation,
  useDeleteLanguageMutation,

  // Query Translations
  useGetQuoteTranslationsQuery,
  useCreateQuoteTranslationMutation,
  useEditQuoteTranslationMutation,
  useDeleteQuoteTranslationMutation,

  // Admin settings
  useGetAllSettingsQuery,
  useUpdateSettingMutation,

  // Quotes
  useGetQuotesQuery,
  useEditQuoteMutation,
  useCreateQuoteMutation,
  useDeleteQuoteMutation,

  // 2FA
  useVerify2FAMutation,
  useCheck2FAStatusQuery,
  useSetup2FAMutation,
  useEnable2FAMutation,
  useDisable2FAMutation,
  useRegenerateBackupCodesMutation,

  // Dashboard
  useGetDashboardQuery,

  // Content performance
  useGetContentPerformanceSummaryQuery,
  useGetContentPerformanceQuotesQuery,
  useGetContentPerformanceAuthorsQuery,
  useGetContentPerformanceBooksQuery,
  useGetContentPerformanceCultureCategoriesQuery,
  useGetContentPerformanceCulturesQuery,
  useGetContentPerformanceTagsQuery,

  // Events
  useGetEventsQuery,
  useLazyGetEventsQuery,
  useGetEventStatsQuery,

  // Trial
  useResetTrialMutation,

  // System Health
  useGetSystemHealthQuery,
} = api;
